import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Package,
  Euro,
  Calendar,
  Shield,
  Heart,
  Zap,
  ChevronRight,
  Smartphone,
  Users,
  ExternalLink,
} from "lucide-react";
import Logo from "@/components/common/Logo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const Index = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-gray-100 bg-white/95 backdrop-blur">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <Logo size="lg" />
          <a
            href="https://www.cleomed.fr/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-cleoMed-secondary hover:text-cleoMed-primary transition-colors"
          >
            En savoir plus →
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-cleoMed-light/50 via-white to-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_#46AB8D08_0%,_transparent_50%)]" />
        <div className="container relative mx-auto px-4 py-16 md:py-24">
          <div className="mx-auto max-w-3xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-cleoMed-primary/30 bg-cleoMed-primary/10 px-4 py-1.5 text-sm font-medium text-cleoMed-primary mb-6"
            >
              <Heart className="h-4 w-4" />
              Rejoignez la communauté
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-4xl font-bold tracking-tight text-cleoMed-secondary md:text-5xl lg:text-6xl"
            >
              Deviens{" "}
              <span className="text-cleoMed-primary">cotransporteur</span>
              <br />
              et aide des patients près de chez toi
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-6 text-lg text-gray-600 md:text-xl"
            >
              Des livraisons en pharmacie • Tu choisis tes créneaux • Tu es
              rémunéré pour chaque course
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-10 flex flex-col gap-4 sm:flex-row sm:justify-center"
            >
              <a
                href="https://apps.apple.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-cleoMed-primary px-8 py-4 font-semibold text-white shadow-lg shadow-cleoMed-primary/25 transition-all hover:bg-cleoMed-primary/90 hover:shadow-xl hover:shadow-cleoMed-primary/30"
              >
                <Smartphone className="h-5 w-5" />
                Télécharger l'app
              </a>
              <a
                href="https://play.google.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-cleoMed-secondary px-8 py-4 font-semibold text-cleoMed-secondary transition-all hover:bg-cleoMed-secondary hover:text-white"
              >
                Disponible sur Google Play
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-cleoMed-secondary md:text-4xl">
              Pourquoi CleoMed ?
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Pas juste une livraison : un service de santé de proximité
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: Heart,
                title: "Mission à impact",
                description:
                  "Aidez les patients à accéder à leurs médicaments en toute sécurité. Une mission utile et gratifiante.",
                color: "bg-rose-100 text-rose-600",
              },
              {
                icon: Euro,
                title: "Un pourboire pour chaque livraison",
                description: "Vos gains sont directement dans votre cagnotte.",
                color: "bg-emerald-100 text-emerald-600",
              },
              {
                icon: Calendar,
                title: "Flexibilité totale",
                description:
                  "Organisez vos livraisons selon votre emploi du temps. Aucun engagement, vous choisissez quand et où.",
                color: "bg-blue-100 text-blue-600",
              },
              {
                icon: Users,
                title: "Parrainage",
                description:
                  "Parrainez des amis et gagnez des bonus lorsqu'ils réalisent des livraisons. Plus vous partagez, plus vous gagnez !",
                color: "bg-amber-100 text-amber-600",
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group rounded-2xl border border-gray-100 bg-white p-8 shadow-sm transition-all hover:shadow-lg hover:border-cleoMed-primary/20"
              >
                <div
                  className={`inline-flex rounded-xl p-3 ${item.color} mb-6`}
                >
                  <item.icon className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-semibold text-cleoMed-secondary">
                  {item.title}
                </h3>
                <p className="mt-3 text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-cleoMed-secondary py-20 text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold md:text-4xl">
              Comment ça fonctionne ?
            </h2>
            <p className="mt-4 text-lg text-white/80">
              Trois étapes pour commencer
            </p>
          </motion.div>

          <div className="mx-auto grid max-w-4xl gap-12 md:grid-cols-3">
            {[
              {
                step: "1",
                title: "Inscrivez-vous et validez votre profil",
                items: [
                  "Renseignez vos documents (RIB, pièce d'identité)",
                  "Complétez votre profil",
                ],
                icon: Shield,
              },
              {
                step: "2",
                title: "Commencez à livrer près de chez vous",
                items: [
                  "Consultez les demandes des pharmacies",
                  "Réservez votre première livraison",
                  "L'app vous guide à chaque étape",
                ],
                icon: Package,
              },
              {
                step: "3",
                title: "Recevez un pourboire pour chaque course",
                items: [
                  "Pourboire calculé selon distance et volume",
                  "Gains dans votre cagnotte",
                  "Transfert direct sur votre compte bancaire",
                ],
                icon: Euro,
              },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="relative text-center"
              >
                <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-white/20">
                  <item.icon className="h-7 w-7" />
                </div>
                <span className="absolute -top-2 -right-2 flex h-8 w-8 items-center justify-center rounded-full bg-cleoMed-primary text-sm font-bold">
                  {item.step}
                </span>
                <h3 className="text-xl font-semibold">{item.title}</h3>
                <ul className="mt-4 space-y-2 text-white/80">
                  {item.items.map((li) => (
                    <li key={li} className="flex items-start gap-2">
                      <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-cleoMed-primary" />
                      {li}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <a
              href="https://apps.apple.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-8 py-4 font-semibold text-cleoMed-secondary transition-all hover:bg-gray-100"
            >
              <Zap className="h-5 w-5" />
              Je télécharge l'app
            </a>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-cleoMed-secondary md:text-4xl">
              Ils nous font confiance
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Rejoignez des d'autres cotransporteurs
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-3">
            {[
              {
                name: "Salomé",
                age: 28,
                text: "L'app me notifie quand une livraison est disponible sur mon trajet. J'accepte ou non. Tout est transparent, et le pourboire est directement visible dans ma cagnotte.",
              },
              {
                name: "Jeanne",
                age: 23,
                text: "Ultra simple, je prends une livraison quand ça m'arrange en rentrant de mon école.",
              },
              {
                name: "Matthieu",
                age: 41,
                text: "Un vrai plus pour rentabiliser mes trajets. J'ai livré plusieurs fois mes voisins qui sont des personnes à mobilité réduite.",
              },
            ].map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="rounded-2xl border border-gray-100 bg-gray-50/50 p-8"
              >
                <p className="text-gray-700 italic">"{t.text}"</p>
                <p className="mt-6 font-semibold text-cleoMed-secondary">
                  {t.name}, {t.age} ans
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-gray-100 bg-gray-50/50 py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto max-w-2xl"
          >
            <h2 className="text-center text-3xl font-bold text-cleoMed-secondary md:text-4xl mb-12">
              Questions fréquentes
            </h2>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="1">
                <AccordionTrigger className="text-left font-medium">
                  Qui peut devenir livreur CleoMed ?
                </AccordionTrigger>
                <AccordionContent>
                  Toute personne majeure pouvant se déplacer et respecter le
                  cadre de confidentialité.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="2">
                <AccordionTrigger className="text-left font-medium">
                  Quels documents sont nécessaires ?
                </AccordionTrigger>
                <AccordionContent>
                  Laissez-vous guider par l'app. Pour valider votre inscription,
                  on vous demande une pièce d'identité et votre RIB.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="3">
                <AccordionTrigger className="text-left font-medium">
                  Combien de temps dure une livraison ?
                </AccordionTrigger>
                <AccordionContent>
                  Cela dépend de la distance entre la pharmacie et la
                  destination. L'app t'indique les infos du trajet avant
                  d'accepter. Compte environ 10 minutes en moyenne.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="4">
                <AccordionTrigger className="text-left font-medium">
                  Comment je gagne de l'argent ?
                </AccordionTrigger>
                <AccordionContent>
                  Chaque course est rémunérée par un pourboire calculé en
                  fonction de la distance parcourue et du volume du colis.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="5">
                <AccordionTrigger className="text-left font-medium">
                  Quand puis-je transférer mes gains ?
                </AccordionTrigger>
                <AccordionContent>
                  À tout moment depuis ta cagnotte. Tes gains seront déposés sur
                  ton compte bancaire.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="6">
                <AccordionTrigger className="text-left font-medium">
                  Comment fonctionne le parrainage ?
                </AccordionTrigger>
                <AccordionContent>
                  Tu peux parrainer des amis en leur partageant ton lien unique.
                  Tu gagnes des bonus à chaque livraison qu'ils effectuent. Une
                  façon simple d'augmenter tes revenus tout en faisant grandir
                  la communauté !
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* CTA Site CleoMed */}
      <section className="border-y border-gray-100 bg-gradient-to-r from-cleoMed-light/30 to-cleoMed-lightBlue/30 py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col items-center justify-between gap-6 rounded-2xl bg-white p-8 shadow-sm md:flex-row md:gap-8"
          >
            <div className="text-center md:text-left">
              <h2 className="text-2xl font-bold text-cleoMed-secondary md:text-3xl">
                Découvrez l'écosystème CleoMed
              </h2>
              <p className="mt-2 text-gray-600">
                Pharmacie, patient, cotransporteur — retrouvez tous nos services
                sur le site.
              </p>
            </div>
            <a
              href="https://www.cleomed.fr/"
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-cleoMed-secondary px-8 py-4 font-semibold text-white shadow-lg transition-all hover:bg-cleoMed-secondary/90 hover:shadow-xl"
            >
              Visiter cleomed.fr
              <ExternalLink className="h-5 w-5" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="bg-cleoMed-primary py-20">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-white md:text-4xl">
              Prêt à rejoindre l'aventure ?
            </h2>
            <p className="mt-4 text-lg text-white/90">
              Téléchargez l'app sur Apple ou Google Store dès aujourd'hui !
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:justify-center">
              <a
                href="https://apps.apple.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-8 py-4 font-semibold text-cleoMed-primary transition-all hover:bg-gray-100"
              >
                <Smartphone className="h-5 w-5" />
                App Store
              </a>
              <a
                href="https://play.google.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white px-8 py-4 font-semibold text-white transition-all hover:bg-white hover:text-cleoMed-primary"
              >
                Google Play
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 bg-white py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <Logo size="sm" />
            <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
              <Link
                to="/forgot-password"
                className="hover:text-cleoMed-primary transition-colors"
              >
                Mot de passe oublié
              </Link>
              <a
                href="https://www.cleomed.fr/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-cleoMed-primary transition-colors"
              >
                À propos
              </a>
              <a
                href="https://www.cleomed.fr/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-cleoMed-primary transition-colors"
              >
                Contact
              </a>
            </div>
          </div>
          <p className="mt-8 text-center text-sm text-gray-400">
            © {new Date().getFullYear()} CleoMed. Tous droits réservés.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
